import { useState } from "react";
import { UserCog } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useRoles, useUpdateUserRoles } from "@/hooks/useTeamManagement";

interface EditUserRolesDialogProps {
  user: {
    id: string;
    display_name: string | null;
    email: string;
    user_role: Array<{
      role_id: string;
      role: {
        id: string;
        name: string;
      };
    }>;
  };
}

export function EditUserRolesDialog({ user }: EditUserRolesDialogProps) {
  const [open, setOpen] = useState(false);
  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  const updateRoles = useUpdateUserRoles();

  const currentRoleIds = user.user_role.map((ur) => ur.role_id);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(currentRoleIds);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      // Reset to current roles when dialog opens
      setSelectedRoleIds(currentRoleIds);
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSave = async () => {
    await updateRoles.mutateAsync({
      userId: user.id,
      roleIds: selectedRoleIds,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-start">
          <UserCog className="h-4 w-4 mr-2" />
          Edit Roles
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User Roles</DialogTitle>
          <DialogDescription>
            Manage roles for {user.display_name || user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isLoadingRoles ? (
            <p className="text-sm text-muted-foreground">Loading roles...</p>
          ) : (
            <div className="space-y-3">
              {roles?.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={role.id}
                    checked={selectedRoleIds.includes(role.id)}
                    onCheckedChange={() => handleRoleToggle(role.id)}
                  />
                  <Label
                    htmlFor={role.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {role.name}
                  </Label>
                </div>
              ))}
            </div>
          )}

          {selectedRoleIds.length === 0 && (
            <p className="text-sm text-destructive">
              User must have at least one role
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateRoles.isPending || selectedRoleIds.length === 0}
          >
            {updateRoles.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
